import React, { useState, useCallback, useEffect } from "react";
import { TimelineContext } from "dnd-timeline";
import { Card, Button, notification, Spin, DatePicker, Modal, Radio, Form } from "antd";
import Timeline from "./Timeline";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { RightOutlined, ReloadOutlined } from "@ant-design/icons";
import moment from "moment-timezone";
import { useAuth } from "services/Auth";
import { offlineHandler } from "utils/offlineCache";

const OccupationMap = (props) => {
  const { auth } = useAuth();
  const apiClient = offlineHandler(auth.data);
  const [form] = Form.useForm();
  const { readOnly = false } = props;
  const [timeframe, setTimeframe] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [itemRows, setItemRows] = useState([]);
  const [services, setServices] = useState([]);
  const [serviceDate, setServiceDate] = useState(null);
  const [staffs, setStaffs] = useState([]);
  const [equipments, setEquipments] = useState([]);
  const [modalSelectDate, setModalSelectDate] = useState({ visible: false, data: null });
  const [itemMode, setItemMode] = useState(null);

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    if (itemMode === "equipment") {
      processItemRows(equipments || []);
    } else if (itemMode === "staff") {
      processItemRows(staffs || []);
    }

    if (serviceDate) {
      loadServices(serviceDate);
    }
  }, [itemMode, serviceDate, equipments]);

  const init = async () => {
    await loadEquipments();
    await loadStaffs();
    setItemMode("equipment");
    form.setFieldsValue({ itemMode: "equipment" });

    if (readOnly === false) {
      if (!serviceDate) setServiceDate(moment().set("hour", 9).set("minute", 0).set("second", 0).set("millisecond", 0).toDate());
    } else {
      const now = moment();
      const sixPM = moment().set("hour", 18).set("minute", 0).set("second", 0).set("millisecond", 0);
      let _serviceDate = moment().set("hour", 9).set("minute", 0).set("second", 0).set("millisecond", 0);
      if (now.isAfter(sixPM)) {
        _serviceDate = moment().add(1, "day").set("hour", 9).set("minute", 0).set("second", 0).set("millisecond", 0);
      }
      setServiceDate(_serviceDate);
    }
  };

  const swapRowMethod = (item) => {
    setItemMode(item);
  };

  const loadStaffs = async () => {
    setIsLoading(true);
    const staffsResponse = await apiClient.getMany("/staff/crew");
    if (!staffsResponse.status) {
      notification.error({
        message: "Erro",
        description: "Erro ao carregar staffs...",
      });
      setIsLoading(false);
      return;
    }

    setStaffs(staffsResponse.data);
    setIsLoading(false);

    if (itemMode === "staff") processItemRows(staffsResponse.data);
  };

  const loadEquipments = async () => {
    setIsLoading(true);

    const equipmentsResponse = await apiClient.getMany("/equipments");

    if (!equipmentsResponse.status) {
      notification.error({
        message: "Erro ao carregar equipamentos",
        description: "Não foi possível carregar os equipamentos",
      });
      setIsLoading(false);
      return;
    }
    const equipments = equipmentsResponse.data.filter((e) => e.order <= 49 && e.equipment_type === "vessel");
    setEquipments(equipments);
    setIsLoading(false);
    if (itemMode === "equipment") processItemRows(equipmentsResponse.data);
  };

  const processItemRows = async (data) => {
    if (!data) {
      setItemRows([]);
      return;
    }
    let rows;

    if (itemMode === "staff") {
      rows = data?.map((item) => ({
        id: item._id,
        name: item.name,
        disabled: false,
        isWorking: item.is_working,
        schedule_start: item.schedule_start,
        schedule_end: item.schedule_end,
        isStaff: true,
      }));
    } else {
      rows = data?.map((item) => ({
        id: item._id,
        name: item.name,
        disabled: false,
        isWorking: true,
        isEq: true,
      }));
    }

    if (!rows) rows = [];

    rows = [{ id: "empty", name: itemMode === "equipment" ? "Sem Equipamento" : "Sem Staff", disabled: false }, ...rows];
    setItemRows(rows);
  };

  const loadServices = async (srv_date) => {
    try {
      if (!srv_date) return;
      setIsLoading(true);

      const service_date = moment(); // moment(srv_date).set("hour", 9).toDate();

      if (!moment(service_date).isValid()) {
        notification.error({
          message: "Erro",
          description: "Data de serviço inválida",
        });
        setIsLoading(false);
        return;
      }

      const todayServicesResponse = await apiClient.getMany("/services/by-day");
      if (!todayServicesResponse.status) {
        notification.error({
          message: "Erro",
          description: "Erro ao carregar serviços de hoje...",
        });
        setIsLoading(false);
        return;
      }

      const multiPassengersResponse = await apiClient.getMany("/entry/occupancy");
      if (!multiPassengersResponse.status) {
        notification.error({
          message: "Erro",
          description: "Erro ao carregar multiPassengersResponse...",
        });
        setIsLoading(false);
        return;
      }

      setTimeframe({
        start: moment(service_date).set("hours", 9).toDate(),
        end: moment(service_date).set("hours", 19).toDate(),
      });

      let servicesWithItem = null;
      if (itemMode === "equipment") {
        servicesWithItem = todayServicesResponse.data
          .filter((el) => el.equipment.length > 0)
          .map((service) =>
            service.equipment.map((equipment) => ({
              id: `${service.service_id_raw}--${equipment._id}`,
              rowId: equipment._id,

              service: service,
              staff: service.staff,
              is_forecast: service.is_forecast,
              is_entry_forecast: service.is_entry_forecast,
              equipment: service.equipment,
              passengers: multiPassengersResponse?.data?.filter((el) => el.service === service.service_id_raw),
              relevance: {
                start: moment(service.final_start_date).set("seconds", 0).set("milliseconds", 0).toDate(),
                end: moment(service.final_end_date).set("seconds", 0).set("milliseconds", 0).toDate(),
              },
              disabled: false,
            })),
          );
      }

      if (itemMode === "staff") {
        servicesWithItem = todayServicesResponse.data
          .filter((el) => el.staff.length > 0)
          .map((service) =>
            service.staff.map((staff) => ({
              id: `${service.service_id_raw}--${staff._id}`,
              rowId: staff._id,

              service: service,
              staff: service.staff,
              is_forecast: service.is_forecast,
              is_entry_forecast: service.is_entry_forecast,
              equipment: service.equipment,
              passengers: multiPassengersResponse?.data?.filter((passenger) => passenger.service === service.service_id_raw),
              relevance: {
                start: moment(service.final_start_date).set("seconds", 0).set("milliseconds", 0).toDate(),
                end: moment(service.final_end_date).set("seconds", 0).set("milliseconds", 0).toDate(),
              },
              disabled: false,
            })),
          );
      }
      // const currentServices = [...emptyServices, ...servicesWithItem].flat();
      const currentServices = [...servicesWithItem].flat();
      const dummyServices = []; // createDummyServices(currentServices, service_date, schedules1H, schedules2H);

      let serviceItems = [...currentServices, ...dummyServices];

      setServices(serviceItems);
      setIsLoading(false);
    } catch (error) {
      notification.error({
        message: "Erro",
        description: "Erro ao carregar serviços...",
      });
      console.log("Error loading services", error);
      setIsLoading(false);
    }
  };

  const onDragEnd = useCallback(
    (event) => {
      if (readOnly === true) return;
    },
    [setServices, serviceDate],
  );

  const getServiceDate = () => {
    const day = moment(serviceDate).isSame(new Date(), "day");
    if (day) return "Hoje";
    return moment(serviceDate).set("hour", 9).format("DD-MM-YYYY");
  };
  return (
    <Spin spinning={isLoading}>
      <Form form={form} id="frm-occupation">
        <Card size={readOnly ? "small" : "default"} style={{ borderWidth: readOnly ? 0 : 1 }} className={readOnly ? "team-occupation-map-card" : ""}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12, alignItems: "center" }}>
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
              <div style={{ marginLeft: 15, marginRight: 15, textAlign: "center" }} onClick={() => setModalSelectDate({ visible: true, data: serviceDate })}>
                <h3 className="m-0">{getServiceDate()}</h3>
              </div>
              {readOnly !== true && (
                <Button
                  style={{ width: readOnly ? "60px" : "80px" }}
                  size={readOnly ? "small" : "large"}
                  type="primary"
                  onClick={() => setServiceDate((prev) => moment(prev).add(1, "day").set("hour", 9).toDate())}
                >
                  <RightOutlined />
                </Button>
              )}
              <Form.Item name="itemMode" noStyle>
                <Radio.Group size={readOnly ? "small" : "large"} buttonStyle="solid" style={{ marginLeft: 10 }} onChange={(e) => swapRowMethod(e.target.value)}>
                  <Radio.Button value="equipment">Equipamento</Radio.Button>
                  <Radio.Button value="staff">Staff</Radio.Button>
                </Radio.Group>
              </Form.Item>
              <Button size={readOnly ? "small" : "large"} style={{ marginLeft: 10 }} type="default" onClick={() => loadServices(serviceDate)}>
                <ReloadOutlined />
              </Button>
            </div>
            <div>
              <Button
                className="mr-2"
                size="small"
                onClick={() => {
                  setTimeframe({
                    start: moment(timeframe.start).subtract(30, "minutes").toDate(),
                    end: moment(timeframe.end).subtract(30, "minutes").toDate(),
                  });
                }}
              >
                {"<<<"}
              </Button>
              <Button
                className="mr-2"
                size="small"
                onClick={() => {
                  setTimeframe({
                    start: moment(timeframe.start).add(30, "minutes").toDate(),
                    end: moment(timeframe.end).add(30, "minutes").toDate(),
                  });
                }}
              >
                {">>>"}
              </Button>
              <Button
                className="mr-2"
                size="small"
                onClick={() => {
                  setTimeframe({
                    start: moment(timeframe.start).subtract(1, "hour").toDate(),
                    end: moment(timeframe.end).add(1, "hour").toDate(),
                  });
                }}
              >
                {"-"}
              </Button>
              <Button
                className="mr-2"
                size="small"
                onClick={() => {
                  setTimeframe({
                    start: moment(timeframe.start).add(1, "hour").toDate(),
                    end: moment(timeframe.end).subtract(1, "hour").toDate(),
                  });
                }}
              >
                {"+"}
              </Button>
            </div>
          </div>
          {timeframe !== null && (
            <TimelineContext
              overlayed={false}
              modifiers={[restrictToVerticalAxis]}
              onDragEnd={onDragEnd}
              onTimeframeChanged={setTimeframe}
              timeframe={timeframe}
              onDragStart={(event) => {
                if (readOnly === true) return false;
              }}
            >
              <Timeline readOnly={readOnly} items={services} rows={itemRows} onDuplicate={() => console.log("DUP")} itemMode={itemMode} />
            </TimelineContext>
          )}
        </Card>

        <Modal
          title="Selecionar Data"
          visible={modalSelectDate.visible}
          onOk={() => setModalSelectDate({ visible: false, data: null })}
          onCancel={() => setModalSelectDate({ visible: false, data: null })}
          footer={<Button onClick={() => setModalSelectDate({ visible: false, data: null })}>Fechar</Button>}
        >
          <DatePicker
            size="large"
            value={moment(modalSelectDate.data)}
            onChange={(date) => {
              setServiceDate(moment(date).set("hour", 9).set("minute", 0).set("second", 0).set("millisecond", 0).toDate());
              setModalSelectDate({ visible: false, data: null });
            }}
          />
        </Modal>
      </Form>
    </Spin>
  );
};

export default OccupationMap;
